import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { Typography } from '@kep-ui-kit/ui-components';
import { Visible24Icon, Invisible24Icon } from '@kep-ui-kit/ui-icons';
import {
  StyledV2Input,
  StyledV2EyeButton,
  StyledV2SubmitBtn,
  StyledV2RequirementsContainer,
  StyledV2Requirement,
  StyledV2RequirementIcon,
} from '@selfserve/react-ui';
import { FormValues } from '../feature/set-password-v2';

const StyledFormArea = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}));

const StyledFieldGroup = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
}));

const StyledPasswordInputWrapper = styled.div({
  position: 'relative',
  width: '100%',
});

const StyledPasswordInput = styled(StyledV2Input)({
  paddingRight: '40px',
  width: '100%',
  boxSizing: 'border-box',
});

export interface Props {
  formik: FormikProps<FormValues>;
  isLoading: boolean;
}

export const SetPasswordFormV2 = ({
  formik: { handleSubmit, handleChange, handleBlur, values, errors, touched },
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isTouched = !!touched.password || !!values.password;
  const passwordErrors = errors.passwordErrors as any;

  const getIconUrl = (hasError: boolean, isTouchedField: boolean): string => {
    if (!isTouchedField) return '/assets/circle.svg';
    return hasError ? '/assets/fail.svg' : '/assets/check.svg';
  };

  return (
    <form onSubmit={handleSubmit}>
      <StyledFormArea>
        <StyledFieldGroup>
          <Typography variant={'body1Highlight' as any}>
            {t('common.password')}
          </Typography>
          <StyledPasswordInputWrapper>
            <StyledPasswordInput
              id="spv2-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              hasError={!!touched.password && !!errors.password}
              autoFocus
              autoComplete="new-password"
            />
            <StyledV2EyeButton
              type="button"
              variant={'borderless'}
              color={'secondary'}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t('aria.hide-password') : t('aria.show-password')}
            >
              {showPassword ? <Invisible24Icon /> : <Visible24Icon />}
            </StyledV2EyeButton>
          </StyledPasswordInputWrapper>
          <StyledV2RequirementsContainer>
            <StyledV2Requirement hasError={!values.password || !!passwordErrors?.minLengthErr} touched={isTouched}>
              <StyledV2RequirementIcon
                src={getIconUrl(!values.password || !!passwordErrors?.minLengthErr, isTouched)}
                hasError={!values.password || !!passwordErrors?.minLengthErr}
                touched={isTouched}
              />
              <p>{t('password-requirements.min-characters')}</p>
            </StyledV2Requirement>
            <StyledV2Requirement hasError={!values.password || !!passwordErrors?.oneNumberErr} touched={isTouched}>
              <StyledV2RequirementIcon
                src={getIconUrl(!values.password || !!passwordErrors?.oneNumberErr, isTouched)}
                hasError={!values.password || !!passwordErrors?.oneNumberErr}
                touched={isTouched}
              />
              <p>{t('password-requirements.number')}</p>
            </StyledV2Requirement>
            <StyledV2Requirement hasError={!values.password || !!passwordErrors?.specialCharErr} touched={isTouched}>
              <StyledV2RequirementIcon
                src={getIconUrl(!values.password || !!passwordErrors?.specialCharErr, isTouched)}
                hasError={!values.password || !!passwordErrors?.specialCharErr}
                touched={isTouched}
              />
              <p>{t('password-requirements.special-char')}</p>
            </StyledV2Requirement>
            <StyledV2Requirement hasError={!values.password || !!passwordErrors?.upperAndLowerCaseErr} touched={isTouched}>
              <StyledV2RequirementIcon
                src={getIconUrl(!values.password || !!passwordErrors?.upperAndLowerCaseErr, isTouched)}
                hasError={!values.password || !!passwordErrors?.upperAndLowerCaseErr}
                touched={isTouched}
              />
              <p>{t('password-requirements.lowercase-uppercase')}</p>
            </StyledV2Requirement>
          </StyledV2RequirementsContainer>
        </StyledFieldGroup>

        <StyledFieldGroup>
          <Typography variant={'body1Highlight' as any}>
            {t('common.confirm-password')}
          </Typography>
          <StyledPasswordInputWrapper>
            <StyledPasswordInput
              id="spv2-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              hasError={!!touched.confirmPassword && !!errors.confirmPassword}
              autoComplete="new-password"
            />
            <StyledV2EyeButton
              type="button"
              variant={'borderless'}
              color={'secondary'}
              onClick={() => setShowConfirmPassword((v) => !v)}
              aria-label={showConfirmPassword ? t('aria.hide-password') : t('aria.show-password')}
            >
              {showConfirmPassword ? <Invisible24Icon /> : <Visible24Icon />}
            </StyledV2EyeButton>
          </StyledPasswordInputWrapper>
          {touched.confirmPassword && errors.confirmPassword && (
            <Typography
              variant={'formError' as any}
              sx={{ mt: 0.5, color: (theme: any) => theme.palette.danger.main, fontWeight: 700 }}
            >
              {errors.confirmPassword}
            </Typography>
          )}
        </StyledFieldGroup>

        <StyledV2SubmitBtn
          type="submit"
          disabled={isLoading}
          fullWidth
        >
          {t('common.set-password')}
        </StyledV2SubmitBtn>
      </StyledFormArea>
    </form>
  );
};
